import React, { useRef, useState } from "react";
import { Animated, TouchableOpacity } from "react-native";
import { Button, DataTable } from "react-native-paper";
import { Entypo, Ionicons, MaterialCommunityIcons } from "@expo/vector-icons";
import WebPageLayout from "../../../../../layouts/WebPageLayout";
import TablePageLayout from "../../../../../layouts/TablePageLayout";
import { BaseFilters } from "../../../../../../web/hooks/useFilters";
import { CoreTable } from "../../../../../components/CoreTable";
import SystemAssetsScreenFilters from "./components/SystemAssetTypesScreenFilters";
import { getSystemScreenHeaders } from "../../../system-screen-headers";
import {
  useDeleteAssetTypeMutation,
  useLazyGetAssetTypesQuery,
} from "../../../../../../../store/services/api/asset-type-api";
import useEntityListPage from "../../../../../hooks/useEntityListPage";
import { ScreenProps } from "../../../../../../../utils/screen-props-helper";
import { SystemAssetsStackParamList } from "../../../assets/SystemAssetsStack";

interface AssetTypeListFilters extends BaseFilters {
  name: string;
}

export default function SystemAssetTypesListScreen({
  navigation,
}: ScreenProps<SystemAssetsStackParamList, "list">) {
  const {
    navigateToCreate,
    navigateToEdit,
    deleteEntity,
    onSubmitFilter,
    filter,
    tableData,
    isLoading,
  } = useEntityListPage<AssetTypeListFilters>({
    entityName: "Asset Type",
    lazyTableQuery: useLazyGetAssetTypesQuery,
    deleteEntityMutation: useDeleteAssetTypeMutation,
    filterDefinition: {
      name: "",
      sortedColumn: "",
      sortedColumnAscending: false,
      pageNumber: 1,
      pageSize: 50,
    },
  });

  const handleOnDeleteEntity = async (data: any) => {
    await deleteEntity(
      data,
      (assetType) => assetType.assetCount === 0,
      `Can't delete Asset Type, there are ${data.assetCount} assets associated with it`
    );
  };

  const navigateToSubAssetType = (id: string) =>
    navigation.navigate("subAssetTypes", { assetTypeId: id });

  // === Filter toggle logic ===
  const [filtersVisible, setFiltersVisible] = useState(true);
  const filtersWidth = useRef(new Animated.Value(filtersVisible ? 1 : 0)).current;

  const toggleFilters = () => {
    const toValue = filtersVisible ? 0 : 1;
    Animated.timing(filtersWidth, {
      toValue,
      duration: 300,
      useNativeDriver: false,
    }).start();
    setFiltersVisible(!filtersVisible);
  };

  return (
    <WebPageLayout header={getSystemScreenHeaders()}>
      <TablePageLayout
        filters={
          <Animated.View
            style={{
              overflow: "hidden",
              width: filtersWidth.interpolate({
                inputRange: [0, 1],
                outputRange: [0, 300], // adjust width as needed
              }),
              height: "100%",
            }}
          >
            <SystemAssetsScreenFilters filter={filter} onSubmit={onSubmitFilter} />
          </Animated.View>
        }
        actions={
          <>
            <Button
              icon={filtersVisible ? "chevron-left" : "chevron-right"}
              mode="contained-tonal"
              onPress={toggleFilters}
              style={{ marginRight: 8 }}
            >
              {filtersVisible ? "Hide Filters" : "Show Filters"}
            </Button>

            <Button
              icon="plus"
              mode="elevated"
              onPress={navigateToCreate}
              buttonColor="#fff"
              textColor="#0f0f0f"
            >
              Add Asset Type
            </Button>
          </>
        }
      >
        <CoreTable<any>
          isLoading={isLoading}
          headers={[
            { text: "Name", sortedName: "name" },
            { text: "Options", flex: 0.5 },
            { text: "Sub Asset Types", flex: 0.5 },
          ]}
          data={tableData}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <DataTable.Row key={item.id}>
              <DataTable.Cell>{item.name}</DataTable.Cell>
              <DataTable.Cell style={{ flex: 0.5 }}>
                <TouchableOpacity onPress={() => navigateToEdit(item.id)}>
                  <Entypo name="pencil" size={24} color="black" />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => handleOnDeleteEntity(item)}>
                  <Ionicons name="ios-remove-circle" size={24} color="red" />
                </TouchableOpacity>
              </DataTable.Cell>
              <DataTable.Cell style={{ flex: 0.5 }}>
                <TouchableOpacity onPress={() => navigateToSubAssetType(item.id)}>
                  <MaterialCommunityIcons
                    name="view-list-outline"
                    size={24}
                    color="black"
                  />
                </TouchableOpacity>
              </DataTable.Cell>
            </DataTable.Row>
          )}
          filters={filter.filters}
          setFilter={filter.setFilter}
        />
      </TablePageLayout>
    </WebPageLayout>
  );
}
